import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { CreateMemoryGameEnterContentTemplate } from '../../components/Templates';
import { QuestionAnswerLanguageSelectPopup } from '../../components/Popups/QuestionAnswerLanguageSelectPopup';
import { LoadingSpinner, ScanTextImageModal } from '../../components/Molecules';

import { usePopup } from '../../context';
import { getLanguageDetail } from '../../constants/languageDetails';
import { SelectImageFileModePopup } from '../../components/Popups';
import { SelectImageMode } from '../../constants/image';
import { BalanceType, TtsLocale } from '../../models/Base';
import {
  getQuickCreateGameModeProps,
  QuickCreateGameMode,
} from '../../models/DraftStore';
import { useDraftStore } from '../../stores/DraftStore';
import { isNotEmptyString } from '../../utils/string';

interface State {
  createType: QuickCreateGameMode | undefined;
  gameContents: string[];
  selectedLanguage: TtsLocale | undefined;
  isNextDisabled: boolean;
  isExceedLimit: boolean;
  showRequiredWarning: boolean;
  scanTextLanguage: TtsLocale | undefined;
  popupMode: 'scanText' | undefined;
  selectedMode: SelectImageMode | undefined;
  scanTextIndex: number | undefined;
  isLoading: boolean;
}

const CreateMemoryGameEnterContentScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();
  const [state, setState] = useState<State>({
    createType: undefined,
    gameContents: [''],
    selectedLanguage: undefined,
    isNextDisabled: true,
    isExceedLimit: false,
    showRequiredWarning: false,
    scanTextLanguage: undefined,
    popupMode: undefined,
    selectedMode: undefined,
    scanTextIndex: undefined,
    isLoading: false,
  });

  const { mode, locale, drafts } = useDraftStore((store) => {
    const _mode = store.getGameMode();
    const _locale = store.getLocale();
    const _gameDrafts = store.getGameContents();
    return { mode: _mode, locale: _locale, drafts: _gameDrafts };
  });

  useEffect(() => {
    if (mode) {
      setState((s) => ({ ...s, createType: mode }));
    } else {
      navigate('/create/memory-game/select-type', { replace: true });
    }
  }, [mode, navigate]);

  useEffect(() => {
    if (locale) {
      setState((s) => ({
        ...s,
        selectedLanguage: locale,
        scanTextLanguage: locale,
      }));
    }
  }, [locale]);

  useEffect(() => {
    if (drafts && drafts.length > 0) {
      const gameContents = drafts.filter((str) => str);
      if (gameContents.length > 0) {
        setState((s) => ({ ...s, gameContents: gameContents }));
      } else {
        setState((s) => ({ ...s, gameContents: [''] }));
      }
    }
  }, [drafts]);

  useEffect(() => {
    const handlePageShow = () => {
      navigate('/create/memory-game/select-type', { replace: true });
    };

    window.addEventListener('popstate', handlePageShow);

    return () => {
      window.removeEventListener('popstate', handlePageShow);
    };
  }, [navigate]);

  const onSelectLanguage = useCallback(
    (language: TtsLocale) => {
      setState((s) => ({ ...s, selectedLanguage: language }));
      useDraftStore.getState().setLocale(language);
      hidePopup();
    },
    [hidePopup],
  );

  const onClickLanguage = useCallback(() => {
    showPopup(
      <QuestionAnswerLanguageSelectPopup
        languageList={
          state.createType
            ? getQuickCreateGameModeProps(state.createType).languageList
            : undefined
        }
        selectedLanguage={state.selectedLanguage}
        onClickOK={hidePopup}
        onCancel={hidePopup}
        onSelectLanguage={onSelectLanguage}
      />,
    );
  }, [
    hidePopup,
    onSelectLanguage,
    showPopup,
    state.createType,
    state.selectedLanguage,
  ]);

  const onGameContentChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>, index: number) => {
      setState((s) => {
        const updatedGameContents = [...s.gameContents];
        updatedGameContents[index] = event.target.value;
        return { ...s, gameContents: updatedGameContents };
      });
    },
    [],
  );

  const gameContents = useMemo(() => {
    return state.gameContents.map((gameContent, index) => {
      return {
        value: gameContent,
        onChange: (event: ChangeEvent<HTMLTextAreaElement>) => {
          onGameContentChange(event, index);
        },
      };
    });
  }, [onGameContentChange, state.gameContents]);

  const onClickExample = useCallback(() => {
    useDraftStore.getState().setGameContents(state.gameContents);
    navigate('/create/memory-game/processed-content-example', {
      replace: true,
    });
  }, [navigate, state.gameContents]);

  const onClickRemove = useCallback((index: number) => {
    setState((s) => ({
      ...s,
      gameContents: s.gameContents.filter((_, i) => index !== i),
    }));
  }, []);

  const onSelectMode = useCallback(
    (selectedMode: SelectImageMode) => {
      hidePopup();
      setState((s) => ({
        ...s,
        popupMode: 'scanText',
        selectedMode: selectedMode,
      }));
    },
    [hidePopup],
  );

  const onClickScanText = useCallback(
    (index: number) => {
      setState((s) => ({
        ...s,
        selectedMode: undefined,
        scanTextIndex: index,
      }));
      showPopup(
        <SelectImageFileModePopup
          onPrimary={hidePopup}
          onSelectMode={onSelectMode}
        />,
      );
    },
    [hidePopup, onSelectMode, showPopup],
  );

  const onClickAddContent = useCallback(() => {
    setState((s) => ({ ...s, gameContents: [...s.gameContents, ''] }));
  }, []);

  useEffect(() => {
    if (state.gameContents.length > 20) {
      setState((s) => ({ ...s, isNextDisabled: true, isExceedLimit: true }));
    } else {
      setState((s) => ({ ...s, isNextDisabled: false, isExceedLimit: false }));
    }
  }, [state.gameContents]);

  const onClickNext = useCallback(() => {
    if (
      state.gameContents.some((gameContent) => !isNotEmptyString(gameContent))
    ) {
      setState((s) => ({ ...s, showRequiredWarning: true }));
    } else {
      setState((s) => ({ ...s, showRequiredWarning: false }));
      useDraftStore.getState().convertContentsToDrafts(state.gameContents);
      navigate('/create/memory-game/processed-content', { replace: true });
    }
  }, [navigate, state.gameContents]);

  const onClickBack = useCallback(() => {
    navigate('/create/memory-game/select-type', { replace: true });
  }, [navigate]);

  const onSelectScanLanguage = useCallback(
    (language: TtsLocale) => {
      setState((s) => ({ ...s, scanTextLanguage: language }));
      hidePopup();
    },
    [hidePopup],
  );

  const onPressCancel = useCallback(() => {
    setState((s) => ({ ...s, popupMode: undefined }));
  }, []);

  const onClickScanLanguage = useCallback(() => {
    showPopup(
      <QuestionAnswerLanguageSelectPopup
        languageList={
          state.createType
            ? getQuickCreateGameModeProps(state.createType).languageList
            : undefined
        }
        selectedLanguage={state.scanTextLanguage}
        onClickOK={hidePopup}
        onCancel={hidePopup}
        onSelectLanguage={onSelectScanLanguage}
      />,
    );
  }, [
    hidePopup,
    onSelectScanLanguage,
    showPopup,
    state.createType,
    state.scanTextLanguage,
  ]);

  const getBase64 = useCallback(
    async (file: File): Promise<string | undefined> => {
      return new Promise<string | undefined>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const base64String = reader.result as string;
          const rawBase64String = base64String.split(',')[1];
          resolve(rawBase64String);
        };
        reader.onerror = (error) => reject(error);
      });
    },
    [],
  );

  const onSelectedFile = useCallback(
    async (file: File | undefined | null) => {
      setState((s) => ({ ...s, popupMode: undefined }));
      if (file) {
        const base64Image = await getBase64(file);
        if (base64Image) {
          setState((s) => ({ ...s, isLoading: true }));
          const detectedText = await useDraftStore
            .getState()
            .scanTextFromImage(base64Image);
          setState((s) => ({ ...s, isLoading: false }));

          const index = state.scanTextIndex;
          if (detectedText && index !== undefined) {
            setState((s) => {
              const updatedGameContents = [...s.gameContents];
              updatedGameContents[index] = detectedText;
              return {
                ...s,
                gameContents: updatedGameContents,
              };
            });
          } else {
            // error fail to get the detectedText or return the null (no text detected)
            toast.error(t('There is something wrong.'));
          }
        } else {
          // error fail to change to base64
          toast.error(t('There is something wrong.'));
          setState((s) => ({ ...s, isLoading: false }));
        }
      }
    },
    [getBase64, state.scanTextIndex, t],
  );

  return (
    <>
      <CreateMemoryGameEnterContentTemplate
        type={state.createType}
        language={getLanguageDetail(state.selectedLanguage)?.displayName ?? ''}
        gameContents={gameContents}
        isNextDisabled={state.isNextDisabled}
        isExceedLimit={state.isExceedLimit}
        showRequiredWarning={state.showRequiredWarning}
        onClickLanguage={onClickLanguage}
        onClickExample={onClickExample}
        onClickRemove={onClickRemove}
        onClickScanText={onClickScanText}
        onClickAddContent={onClickAddContent}
        onClickNext={onClickNext}
        onClickBack={onClickBack}
      />
      {state.popupMode === 'scanText' && (
        <ScanTextImageModal
          selectedMode={state.selectedMode}
          language={
            getLanguageDetail(state.scanTextLanguage)?.displayName ?? ''
          }
          currencyType={BalanceType.CreationToken}
          fee={1}
          onPrimary={(file: File | null | undefined) => {
            onSelectedFile(file).catch(console.error);
          }}
          onClickLanguage={onClickScanLanguage}
          onPressCancel={onPressCancel}
        />
      )}
      {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
    </>
  );
};
export default CreateMemoryGameEnterContentScreen;
